// Container for all elements to be rendered in the App
import React, { Component } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';

// Navigation
import AppBar from './AppBar';
import AlertBanner from './AlertBanner';
import AlertList from './AlertList';
import PillNav from './PillNav';

// Constants
import { T } from '../constants/tokens';

// Pages
import DashboardMap from '../pages/dashboard/map';
import DashboardList from '../pages/dashboard/list';
import DashboardOverview from '../pages/dashboard/overview';
import NetworkPerformance from '../pages/network';
import AlertHistory from '../pages/dashboard/alerts';
import AlertDetail from '../pages/dashboard/alerts/[id]';
import AccessLog from '../pages/admin/access';
import ActivityReporter from './ActivityReporter';
import FleetPoller from './FleetPoller';

import ChangePassword from '../pages/account/change-password';
import AccountSettings from '../pages/account/settings';

import Login from '../pages/auth/login';
import ForgotPassword from '../pages/auth/forgot-password';
import ResetPassword from '../pages/auth/reset-password';
import TwoFactorSetup from '../pages/auth/two-factor/setup';
import TwoFactor from '../pages/auth/two-factor';

import AssignBadges from '../pages/admin/badges/assign';
import Badge from '../pages/admin/badges/[id]';
import Badges from '../pages/admin/badges';
import CreateBuildings from '../pages/admin/buildings/create';
import Building from '../pages/admin/buildings/[id]';
import Buildings from '../pages/admin/buildings';
import Floor from '../pages/admin/floors/[id]';
import CreateFloors from '../pages/admin/floors/create';
import Floors from '../pages/admin/floors';
import AssignHubs from '../pages/admin/hubs/assign';
import Hub from '../pages/admin/hubs/[id]';
import Hubs from '../pages/admin/hubs';
import AddRedAlertTextContact from '../pages/admin/red-alert-text-groups/[id]/add';
import RedAlertTextGroups from '../pages/admin/red-alert-text-groups';
import Room from '../pages/admin/rooms/[id]';
import CreateRooms from '../pages/admin/rooms/create';
import Rooms from '../pages/admin/rooms';
import StaffMember from '../pages/admin/staff/[id]';
import CreateStaff from '../pages/admin/staff/create';
import Staff from '../pages/admin/staff';
import CreateUsers from '../pages/admin/users/create';
import Users from '../pages/admin/users';
import Config from '../pages/admin';

import PageWrapper from './PageWrapper';

// Components
import StatusAlertModal from '../components/StatusAlertModal';
import { hideAlertModal } from '../redux/actions/statusActions';

const routeAndWrapperMapStateToProps = state => ({
  windowSize: state.ui.windowSize,
  userType: state.auth.userType,
  isMobile: state.ui.isMobile,
});

/**
 * One route inside the shell.
 *
 * Before v7 each route rendered its own copy of the side menu beside its
 * children. The rail is now part of the shell, above the Switch, so this is
 * just the auth gate plus the content column. `sideMenu` is kept as a prop
 * because every call site passes it, but it no longer selects a layout.
 */
class RouteAndWrapper extends Component {
  render() {
    return (
      <Route path={this.props.path}>
        <PageWrapper>
          {/* PillNav decides for itself whether this route gets pills.
              PageWrapper takes a single child, so keep the fragment. */}
          <>
            <PillNav />
            {this.props.children}
          </>
        </PageWrapper>
      </Route>
    );
  }
}

RouteAndWrapper.propTypes = {
  children: PropTypes.any.isRequired,
  path: PropTypes.string.isRequired,
  sideMenu: PropTypes.bool,
  windowSize: PropTypes.object.isRequired,
  userType: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  isRail: PropTypes.bool
};

var ConnectedRouteAndWrapper = connect(routeAndWrapperMapStateToProps, null)(RouteAndWrapper);

// Props: windowSize
class Navigation extends Component {

  constructor(props) {
    super(props);

    this.dismissAlert = this.dismissAlert.bind(this);
  }

  dismissAlert() {
    this.props.hideAlertModal();
  }

  render() {
    return (
      <>
        <StatusAlertModal isVisible={this.props.showAlertModal} alertStatus={this.props.schoolStatus} dismissAlert={this.dismissAlert} />
        {/* A single column. The design has no rail: its `showRail` is false
            and the shell grid is one track, with the drawer as the only
            navigation surface. */}
        <div style={{ minHeight: '100vh', background: T.canvas, display: 'flex', flexDirection: 'column' }}>
          <AppBar />
          <div style={{ flex: 1, minWidth: 0 }}>
            <AlertBanner />
            <AlertList />
            <ActivityReporter enabled={this.props.isAuthenticated === true} />
            <FleetPoller enabled={this.props.isAuthenticated === true} />
            {/* The design's content column: 1200px max, 32px gutter. */}
            <div style={{ maxWidth: 1200, margin: '0 auto', minWidth: 0, padding: 32 }}>
              <Switch>
                {/* Config */}
                <ConnectedRouteAndWrapper path='/admin/badges/assign' sideMenu>
                  <AssignBadges />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/badges/:id' sideMenu>
                  <Badge />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/badges' sideMenu>
                  <Badges />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/buildings/create' sideMenu>
                  <CreateBuildings />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/buildings/:id' sideMenu>
                  <Building />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/buildings' sideMenu>
                  <Buildings />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/floors/create' sideMenu>
                  <CreateFloors />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/floors/:id' sideMenu>
                  <Floor />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/floors' sideMenu>
                  <Floors />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/hubs/assign' sideMenu>
                  <AssignHubs />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/hubs/:id' sideMenu>
                  <Hub />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/hubs' sideMenu>
                  <Hubs />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/red-alert-text-groups/:id/add' sideMenu>
                  <AddRedAlertTextContact />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/red-alert-text-groups' sideMenu>
                  <RedAlertTextGroups />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/rooms/create' sideMenu>
                  <CreateRooms />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/rooms/:id' sideMenu>
                  <Room />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/rooms' sideMenu>
                  <Rooms />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/staff/create' sideMenu>
                  <CreateStaff />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/staff/:id' sideMenu>
                  <StaffMember />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/staff' sideMenu>
                  <Staff />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/users/create/:user_id' sideMenu>
                  <CreateUsers />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/users/create' sideMenu>
                  <CreateUsers />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin/users' sideMenu>
                  <Users />
                </ConnectedRouteAndWrapper>
                {/* Before /admin, which would otherwise match it. */}
                <ConnectedRouteAndWrapper path='/admin/access' sideMenu>
                  <AccessLog />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/admin' sideMenu>
                  <Config />
                </ConnectedRouteAndWrapper>

                {/* Account */}
                <ConnectedRouteAndWrapper path='/account/change-password'>
                  <ChangePassword />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/account/settings' sideMenu>
                  <AccountSettings />
                </ConnectedRouteAndWrapper>

                {/* Auth */}
                <ConnectedRouteAndWrapper path='/login'>
                  <Redirect to='/auth/login' />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/auth/login'>
                  <Login />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/auth/forgot-password'>
                  <ForgotPassword />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/auth/reset-password'>
                  <ResetPassword />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/auth/two-factor/setup'>
                  <TwoFactorSetup />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/auth/two-factor'>
                  <TwoFactor />
                </ConnectedRouteAndWrapper>

                {/* Dashboard */}
                <ConnectedRouteAndWrapper path='/dashboard/overview'>
                  <DashboardOverview />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/dashboard/map'>
                  <DashboardMap />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/dashboard/devices'>
                  <DashboardList mode='devices' />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/dashboard/people'>
                  <DashboardList mode='people' />
                </ConnectedRouteAndWrapper>
                {/* Detail before the list: routes match on prefix. */}
                <ConnectedRouteAndWrapper path='/dashboard/alerts/:id'>
                  <AlertDetail />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/dashboard/alerts'>
                  <AlertHistory />
                </ConnectedRouteAndWrapper>
                {/* Super users only; the page redirects anyone else. */}
                <ConnectedRouteAndWrapper path='/network'>
                  <NetworkPerformance />
                </ConnectedRouteAndWrapper>
                {/* The old combined list, so existing links still land
                    somewhere sensible. */}
                <ConnectedRouteAndWrapper path='/dashboard/list'>
                  <Redirect to='/dashboard/devices' />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/' exact>
                  <Redirect to='/dashboard/overview' />
                </ConnectedRouteAndWrapper>
                <ConnectedRouteAndWrapper path='/'>
                  <Redirect to='/dashboard/overview' />
                </ConnectedRouteAndWrapper>
              </Switch>
            </div>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = state => ({
  schoolStatus: state.status.schoolStatus,
  showAlertModal: state.status.showAlertModal,
  isAuthenticated: state.auth.isAuthenticated,
});

const mapDispatchToProps = dispatch => ({
  hideAlertModal: () => hideAlertModal(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Navigation);

Navigation.propTypes = {
  hideAlertModal: PropTypes.func,
  showAlertModal: PropTypes.bool,
  schoolStatus: PropTypes.object,
  isAuthenticated: PropTypes.bool
};
